import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  LayoutDashboard, BarChart2, Calendar, FileSpreadsheet, Settings,
  RefreshCw, Download, FileText, Clock, CheckCircle, XCircle,
  IndianRupee, ChevronRight, ExternalLink, Plus, Trash2, GripVertical,
  User, Star, Lightbulb, Save, Upload, ChevronDown, ChevronUp
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CHART_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const STATUS_COLORS = { pending: '#f59e0b', approved: '#10b981', rejected: '#ef4444', under_review: '#2563eb', needs_revision: '#f97316' };
const STATUS_BG = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700', under_review: 'bg-blue-100 text-blue-700' };
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const NAV = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'quarterly', label: 'Quarterly Analysis', icon: BarChart2 },
  { id: 'yearly', label: 'Yearly Comparison', icon: Calendar },
  { id: 'reports', label: 'Detailed Reports', icon: FileSpreadsheet },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f172a] border border-slate-700 rounded-sm p-3 shadow-xl">
      <p className="font-data text-[10px] text-slate-400 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs font-semibold" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
};

function KpiCard({ label, value, color = 'text-white', icon: Icon }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4 min-w-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-medium uppercase tracking-widest text-slate-400 truncate pr-1">{label}</span>
        {Icon && <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />}
      </div>
      <p className={`font-data text-xl font-bold ${color} truncate`}>{value ?? '--'}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-sm overflow-hidden">
      <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/60 transition-colors" onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-blue-400" />}
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

// ─── SORTABLE KPI ITEM ─────────────────────────────────────────
function SortableKpiItem({ id, kpi, index, fieldOptions, onUpdate, onRemove, inputClass, selectClass }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 bg-slate-900/30 rounded-sm p-1.5" data-testid={`kpi-row-${index}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-700 rounded transition-colors" data-testid={`kpi-drag-${index}`}>
        <GripVertical className="w-4 h-4 text-slate-500" />
      </button>
      <Select value={kpi.field} onValueChange={v => onUpdate(index, 'field', v)}>
        <SelectTrigger className={`${selectClass} flex-1 min-w-0`}><SelectValue placeholder="Pick field..." /></SelectTrigger>
        <SelectContent className="bg-slate-800 border-slate-600">
          {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input value={kpi.label} onChange={e => onUpdate(index, 'label', e.target.value)} className={`${inputClass} w-36`} placeholder="Display name" />
      <Input value={kpi.unit} onChange={e => onUpdate(index, 'unit', e.target.value)} className={`${inputClass} w-16`} placeholder="Unit" title="Unit (e.g. ₹, %, /5)" />
      <button onClick={() => onRemove(index)} className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0 p-0.5" data-testid={`kpi-remove-${index}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── SORTABLE METRIC ITEM ──────────────────────────────────────
function SortableMetricItem({ id, row, index, fieldOptions, onUpdate, onRemove, inputClass, selectClass }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 bg-slate-900/30 rounded-sm p-1.5" data-testid={`metric-row-${index}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-700 rounded transition-colors" data-testid={`metric-drag-${index}`}>
        <GripVertical className="w-4 h-4 text-slate-500" />
      </button>
      <Select value={row.field} onValueChange={v => onUpdate(index, 'field', v)}>
        <SelectTrigger className={`${selectClass} flex-1 min-w-0`}><SelectValue placeholder="Pick field..." /></SelectTrigger>
        <SelectContent className="bg-slate-800 border-slate-600">
          {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input value={row.label} onChange={e => onUpdate(index, 'label', e.target.value)} className={`${inputClass} w-48`} placeholder="Display name" />
      <button onClick={() => onRemove(index)} className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0 p-0.5" data-testid={`metric-remove-${index}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── SETTINGS TAB ───────────────────────────────────────────────
function SettingsTab({ formId, config, availableFields, onSaved }) {
  const [form, setForm] = useState({
    dashboard_title: '',
    period_label: '',
    date_field: '',
    submitter_name_field: '',
    emp_code_field: '',
    email_field: '',
    kpi_fields: [],
    metric_rows: [],
    profile: { photo_url: '', manual_name: '', manual_emp_code: '', manual_email: '' },
    key_insights: [],
    overall_rating: 0,
    overall_rating_label: '',
  });
  const [saving, setSaving] = useState(false);
  const photoRef = useRef();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (config) {
      setForm({
        dashboard_title: config.dashboard_title || '',
        period_label: config.period_label || '',
        date_field: config.date_field || '',
        submitter_name_field: config.submitter_name_field || '',
        emp_code_field: config.emp_code_field || '',
        email_field: config.email_field || '',
        kpi_fields: config.kpi_fields?.length ? config.kpi_fields.map((k, i) => ({ ...k, _id: `kpi-${i}-${Date.now()}` })) : [],
        metric_rows: config.metric_rows?.length ? config.metric_rows.map((m, i) => ({ ...m, _id: `metric-${i}-${Date.now()}` })) : [],
        profile: config.profile || { photo_url: '', manual_name: '', manual_emp_code: '', manual_email: '' },
        key_insights: config.key_insights?.length ? config.key_insights : [],
        overall_rating: config.overall_rating || 0,
        overall_rating_label: config.overall_rating_label || '',
      });
    }
  }, [config]);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const updProfile = (key, val) => setForm(f => ({ ...f, profile: { ...f.profile, [key]: val } }));

  // KPI fields
  const addKpi = () => setForm(f => ({ ...f, kpi_fields: [...f.kpi_fields, { _id: `kpi-${Date.now()}`, field: '', label: '', unit: '' }] }));
  const updKpi = (i, k, v) => setForm(f => { const a = [...f.kpi_fields]; a[i] = { ...a[i], [k]: v }; return { ...f, kpi_fields: a }; });
  const removeKpi = (i) => setForm(f => ({ ...f, kpi_fields: f.kpi_fields.filter((_, idx) => idx !== i) }));

  // KPI drag end
  const handleKpiDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setForm(f => {
        const oldIndex = f.kpi_fields.findIndex(k => k._id === active.id);
        const newIndex = f.kpi_fields.findIndex(k => k._id === over.id);
        return { ...f, kpi_fields: arrayMove(f.kpi_fields, oldIndex, newIndex) };
      });
    }
  };

  // Metric rows
  const addMetric = () => setForm(f => ({ ...f, metric_rows: [...f.metric_rows, { _id: `metric-${Date.now()}`, field: '', label: '' }] }));
  const updMetric = (i, k, v) => setForm(f => { const a = [...f.metric_rows]; a[i] = { ...a[i], [k]: v }; return { ...f, metric_rows: a }; });
  const removeMetric = (i) => setForm(f => ({ ...f, metric_rows: f.metric_rows.filter((_, idx) => idx !== i) }));

  // Metric drag end
  const handleMetricDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setForm(f => {
        const oldIndex = f.metric_rows.findIndex(m => m._id === active.id);
        const newIndex = f.metric_rows.findIndex(m => m._id === over.id);
        return { ...f, metric_rows: arrayMove(f.metric_rows, oldIndex, newIndex) };
      });
    }
  };

  // Key insights
  const addInsight = () => setForm(f => ({ ...f, key_insights: [...f.key_insights, ''] }));
  const updInsight = (i, v) => setForm(f => { const a = [...f.key_insights]; a[i] = v; return { ...f, key_insights: a }; });
  const removeInsight = (i) => setForm(f => ({ ...f, key_insights: f.key_insights.filter((_, idx) => idx !== i) }));

  // Photo upload → base64
  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Photo must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => updProfile('photo_url', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Remove _id from kpi_fields and metric_rows before saving
      const payload = {
        ...form,
        kpi_fields: form.kpi_fields.map(({ _id, ...rest }) => rest),
        metric_rows: form.metric_rows.map(({ _id, ...rest }) => rest),
      };
      await api.put(`/form-dashboard/${formId}/config`, payload);
      toast.success('Dashboard settings saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fieldOptions = availableFields || [];

  const labelClass = 'text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1 block';
  const inputClass = 'bg-slate-900 border-slate-600 text-slate-100 placeholder-slate-500 text-sm rounded-sm h-9 focus-visible:ring-blue-500/40';
  const selectClass = 'bg-slate-900 border-slate-600 text-slate-100 text-sm rounded-sm h-9';

  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-tab">
      {/* Section A: Dashboard Identity */}
      <Section title="Dashboard Identity" icon={LayoutDashboard}>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2">
            <label className={labelClass}>Dashboard Title</label>
            <Input value={form.dashboard_title} onChange={e => upd('dashboard_title', e.target.value)}
              className={inputClass} placeholder="e.g. Management Level Monthly Sales Audit" data-testid="settings-title" />
          </div>
          <div>
            <label className={labelClass}>Period Label</label>
            <Input value={form.period_label} onChange={e => upd('period_label', e.target.value)}
              className={inputClass} placeholder="e.g. Q1 FY2026 (Apr – Jun 2026)" data-testid="settings-period" />
          </div>
          <div>
            <label className={labelClass}>Date Field (groups by month)</label>
            <Select value={form.date_field || "__none__"} onValueChange={v => upd('date_field', v === "__none__" ? "" : v)}>
              <SelectTrigger className={selectClass} data-testid="settings-date-field"><SelectValue placeholder="Select field..." /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="__none__" className="text-slate-300 text-xs">— None —</SelectItem>
                {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelClass}>Submitter Name Field</label>
            <Select value={form.submitter_name_field || "__none__"} onValueChange={v => upd('submitter_name_field', v === "__none__" ? "" : v)}>
              <SelectTrigger className={selectClass}><SelectValue placeholder="Select field..." /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="__none__" className="text-slate-300 text-xs">— None —</SelectItem>
                {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelClass}>Emp Code Field</label>
            <Select value={form.emp_code_field || "__none__"} onValueChange={v => upd('emp_code_field', v === "__none__" ? "" : v)}>
              <SelectTrigger className={selectClass}><SelectValue placeholder="Select field..." /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="__none__" className="text-slate-300 text-xs">— None —</SelectItem>
                {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelClass}>Email Field</label>
            <Select value={form.email_field || "__none__"} onValueChange={v => upd('email_field', v === "__none__" ? "" : v)}>
              <SelectTrigger className={selectClass}><SelectValue placeholder="Select field..." /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="__none__" className="text-slate-300 text-xs">— None —</SelectItem>
                {fieldOptions.map(f => <SelectItem key={f} value={f} className="text-slate-200 text-xs focus:bg-slate-700">{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* Section B: KPI Cards with Drag & Drop */}
      <Section title="KPI Cards (up to 6) — Drag to Reorder" icon={BarChart2}>
        <p className="text-[10px] text-slate-500 mb-3">These fields appear as large number cards at the top of the dashboard. Drag items to reorder.</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleKpiDragEnd}>
          <SortableContext items={form.kpi_fields.map(k => k._id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {form.kpi_fields.map((kpi, i) => (
                <SortableKpiItem
                  key={kpi._id}
                  id={kpi._id}
                  kpi={kpi}
                  index={i}
                  fieldOptions={fieldOptions}
                  onUpdate={updKpi}
                  onRemove={removeKpi}
                  inputClass={inputClass}
                  selectClass={selectClass}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {form.kpi_fields.length < 6 && (
          <button onClick={addKpi} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 mt-3 transition-colors" data-testid="add-kpi-btn">
            <Plus className="w-3.5 h-3.5" /> Add KPI Field
          </button>
        )}
      </Section>

      {/* Section C: Metric Rows with Drag & Drop */}
      <Section title="Metric Rows (Monthly Column View) — Drag to Reorder" icon={FileSpreadsheet}>
        <p className="text-[10px] text-slate-500 mb-3">Fields that appear as rows in the monthly columns layout. Drag items to reorder.</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMetricDragEnd}>
          <SortableContext items={form.metric_rows.map(m => m._id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {form.metric_rows.map((row, i) => (
                <SortableMetricItem
                  key={row._id}
                  id={row._id}
                  row={row}
                  index={i}
                  fieldOptions={fieldOptions}
                  onUpdate={updMetric}
                  onRemove={removeMetric}
                  inputClass={inputClass}
                  selectClass={selectClass}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button onClick={addMetric} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 mt-3 transition-colors" data-testid="add-metric-btn">
          <Plus className="w-3.5 h-3.5" /> Add Metric Row
        </button>
      </Section>

      {/* Section D: Team Lead Profile */}
      <Section title="Team Lead Profile" icon={User}>
        <div className="space-y-4 mt-2">
          {/* Auto-pulled info */}
          <div>
            <p className="text-[10px] text-slate-500 mb-2">Auto-pulled from form fields (configure above in Identity section). Leave manual fields blank to use these.</p>
            <div className="flex flex-wrap gap-2">
              {form.submitter_name_field && <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-1 rounded-sm">Name: from "{form.submitter_name_field}"</span>}
              {form.emp_code_field && <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-1 rounded-sm">Code: from "{form.emp_code_field}"</span>}
              {form.email_field && <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-1 rounded-sm">Email: from "{form.email_field}"</span>}
              {!form.submitter_name_field && !form.emp_code_field && !form.email_field && <span className="text-[10px] text-slate-600">Set name/code/email fields above first</span>}
            </div>
          </div>
          {/* Manual overrides */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Override Name</label>
              <Input value={form.profile.manual_name} onChange={e => updProfile('manual_name', e.target.value)}
                className={inputClass} placeholder="Leave blank to auto-pull" data-testid="profile-manual-name" />
            </div>
            <div>
              <label className={labelClass}>Override Emp Code</label>
              <Input value={form.profile.manual_emp_code} onChange={e => updProfile('manual_emp_code', e.target.value)}
                className={inputClass} placeholder="Leave blank to auto-pull" />
            </div>
            <div>
              <label className={labelClass}>Override Email</label>
              <Input value={form.profile.manual_email} onChange={e => updProfile('manual_email', e.target.value)}
                className={inputClass} placeholder="Leave blank to auto-pull" />
            </div>
          </div>
          {/* Photo upload */}
          <div>
            <label className={labelClass}>Profile Photo</label>
            <div className="flex items-center gap-4">
              {form.profile.photo_url ? (
                <div className="relative">
                  <img src={form.profile.photo_url} alt="Profile" className="w-16 h-16 rounded-sm object-cover border border-slate-600" />
                  <button onClick={() => updProfile('photo_url', '')} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center" title="Remove">
                    <XCircle className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-sm bg-slate-700 border border-slate-600 flex items-center justify-center">
                  <User className="w-6 h-6 text-slate-500" />
                </div>
              )}
              <div>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="photo-upload-input" />
                <Button size="sm" variant="outline" className="rounded-sm bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 text-xs gap-1.5"
                  onClick={() => photoRef.current?.click()} data-testid="upload-photo-btn">
                  <Upload className="w-3.5 h-3.5" /> Upload Photo
                </Button>
                <p className="text-[10px] text-slate-500 mt-1">Max 500KB. JPG/PNG.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Section E: Key Insights & Rating */}
      <Section title="Key Insights & Overall Rating" icon={Lightbulb}>
        <div className="space-y-4 mt-2">
          <div>
            <label className={labelClass}>Key Insights (bullet points)</label>
            <div className="space-y-2">
              {form.key_insights.map((insight, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">•</span>
                  <Input value={insight} onChange={e => updInsight(i, e.target.value)}
                    className={`${inputClass} flex-1`} placeholder="e.g. Revenue Target Exceeded" data-testid={`insight-${i}`} />
                  <button onClick={() => removeInsight(i)} className="text-slate-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={addInsight} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid="add-insight-btn">
                <Plus className="w-3.5 h-3.5" /> Add Insight
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Overall Rating (0–5)</label>
              <Input type="number" min="0" max="5" step="0.1"
                value={form.overall_rating} onChange={e => upd('overall_rating', parseFloat(e.target.value) || 0)}
                className={inputClass} data-testid="overall-rating-input" />
            </div>
            <div>
              <label className={labelClass}>Rating Label</label>
              <Input value={form.overall_rating_label} onChange={e => upd('overall_rating_label', e.target.value)}
                className={inputClass} placeholder="e.g. Strong Growth Quarter" data-testid="rating-label-input" />
            </div>
          </div>
        </div>
      </Section>

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={saving} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="save-settings-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}

// ─── OVERVIEW TAB ───────────────────────────────────────────────
function OverviewTab({ data, config }) {
  if (!data) return <div className="flex items-center justify-center h-64"><p className="text-slate-400 text-sm">Loading...</p></div>;
  const { kpis, monthly_trend, status_breakdown, top_submitters, recent_submissions } = data;
  const hasConfig = config?.configured && config.kpi_fields?.length > 0;

  // Compute configured KPI values from latest submission's mapped_data
  const latestSub = recent_submissions?.[0];
  const latestMapped = latestSub?.mapped_data || {};

  // Auto-pull profile info from latest submission
  const profileName = config?.profile?.manual_name || (config?.submitter_name_field ? latestMapped[config.submitter_name_field] : null) || null;
  const profileCode = config?.profile?.manual_emp_code || (config?.emp_code_field ? latestMapped[config.emp_code_field] : null) || null;
  const profileEmail = config?.profile?.manual_email || (config?.email_field ? latestMapped[config.email_field] : null) || null;
  const profilePhoto = config?.profile?.photo_url || null;
  const hasProfile = profileName || profileCode || profileEmail || profilePhoto;

  return (
    <div className="space-y-6">
      {/* Profile card — shown if configured */}
      {hasProfile && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4 flex items-center gap-5" data-testid="profile-card">
          {profilePhoto ? (
            <img src={profilePhoto} alt={profileName} className="w-14 h-14 rounded-sm object-cover border-2 border-blue-500/40 flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-sm bg-slate-700 border-2 border-slate-600 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-slate-500" />
            </div>
          )}
          <div>
            {profileName && <p className="text-white font-semibold text-sm">{profileName}</p>}
            {profileCode && <p className="text-slate-400 text-xs font-data mt-0.5">Emp Code: {profileCode}</p>}
            {profileEmail && <p className="text-slate-400 text-xs font-data">{profileEmail}</p>}
            {config?.period_label && <Badge className="mt-1.5 text-[9px] bg-blue-600/20 text-blue-300 border border-blue-500/30 rounded-sm">{config.period_label}</Badge>}
          </div>
        </div>
      )}

      {/* KPIs — configured or generic */}
      {hasConfig ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="form-kpi-cards">
          {config.kpi_fields.map((kf, i) => {
            const rawVal = latestMapped[kf.field];
            const numVal = parseFloat(rawVal);
            const display = rawVal != null ? `${kf.unit || ''}${!isNaN(numVal) ? numVal.toLocaleString('en-IN') : rawVal}` : '--';
            return <KpiCard key={i} label={kf.label || kf.field} value={display} color={i === 0 ? 'text-white' : i === 1 ? 'text-emerald-400' : i === 2 ? 'text-blue-400' : 'text-amber-400'} />;
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="form-kpi-cards">
          <KpiCard label="Total" value={kpis.total} icon={FileText} color="text-white" />
          <KpiCard label="Pending" value={kpis.pending} icon={Clock} color="text-amber-400" />
          <KpiCard label="Approved" value={kpis.approved} icon={CheckCircle} color="text-emerald-400" />
          <KpiCard label="Rejected" value={kpis.rejected} icon={XCircle} color="text-red-400" />
          <KpiCard label="Total Amount" value={`₹${kpis.total_amount?.toLocaleString('en-IN') || 0}`} icon={IndianRupee} color="text-blue-400" />
        </div>
      )}

      {/* Key insights & rating — if configured */}
      {config?.configured && (config.key_insights?.length > 0 || config.overall_rating > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {config.key_insights?.length > 0 && (
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-amber-400" /><h3 className="text-sm font-semibold text-white">Key Insights</h3></div>
              <ul className="space-y-1.5">
                {config.key_insights.map((ins, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300"><span className="text-blue-400 mt-0.5">•</span>{ins}</li>
                ))}
              </ul>
            </div>
          )}
          {config.overall_rating > 0 && (
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-amber-400" /><h3 className="text-sm font-semibold text-white">Overall Rating</h3></div>
              <div className="flex items-center gap-2">
                <span className="font-data text-3xl font-bold text-amber-400">{config.overall_rating}</span>
                <span className="text-slate-400 text-sm">/5</span>
                <div className="flex ml-1">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} className={`w-5 h-5 ${n <= Math.round(config.overall_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                  ))}
                </div>
              </div>
              {config.overall_rating_label && <p className="text-xs text-slate-400 mt-1">{config.overall_rating_label}</p>}
            </div>
          )}
        </div>
      )}

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-slate-800/60 border border-slate-700 rounded-sm p-4">
          <h3 className="font-heading text-sm font-semibold text-white mb-4">Submission Trend</h3>
          {monthly_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthly_trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="count" name="Submissions" stroke="#2563eb" strokeWidth={2} dot={{ r: 3, fill: '#2563eb' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-500 text-sm">No trend data yet</div>
          )}
        </div>
        <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
          <h3 className="font-heading text-sm font-semibold text-white mb-4">Status Breakdown</h3>
          {status_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}>
                  {status_breakdown.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || CHART_COLORS[i]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
                <Legend formatter={v => <span className="text-[10px] text-slate-300 font-data capitalize">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-500 text-sm">No data</div>
          )}
        </div>
      </div>

      {/* Top submitters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
          <h3 className="font-heading text-sm font-semibold text-white mb-4">Top Submitters</h3>
          {top_submitters.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={top_submitters} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} width={80} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="count" name="Submissions" fill="#2563eb" radius={[0, 2, 2, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-500 text-sm">No submitter data</div>
          )}
        </div>
        <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
          <h3 className="font-heading text-sm font-semibold text-white mb-4">Recent Submissions</h3>
          {recent_submissions.length > 0 ? (
            <div className="space-y-2">
              {recent_submissions.slice(0, 5).map(s => (
                <div key={s.id} className="flex items-center justify-between py-1.5 border-b border-slate-700/60 cursor-pointer hover:bg-slate-700/30 px-1 rounded-sm transition-colors" onClick={() => window.open(`/submissions/${s.id}`, '_blank')}>
                  <span className="text-xs text-slate-300 truncate">{s.submitted_by_name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <Badge className={`text-[9px] rounded-sm ${STATUS_BG[s.status] || 'bg-slate-700 text-slate-300'}`}>{s.status}</Badge>
                    <span className="font-data text-[10px] text-slate-500">{formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-500 text-sm">No submissions yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── QUARTERLY TAB ──────────────────────────────────────────────
function QuarterlyTab({ formId, showTest }) {
  const currentYear = new Date().getFullYear();
  const currentQ = Math.ceil((new Date().getMonth() + 1) / 3);
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/form-dashboard/${formId}/quarterly`, { params: { year, quarter, show_test: showTest } })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [formId, year, quarter, showTest]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
          <SelectTrigger className="w-28 h-8 rounded-sm bg-slate-800 border-slate-600 text-slate-200 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-600">
            {[currentYear, currentYear - 1, currentYear - 2].map(y => (
              <SelectItem key={y} value={String(y)} className="text-slate-200 text-xs focus:bg-slate-700">{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map(q => (
            <button key={q} onClick={() => setQuarter(q)}
              className={`px-3 py-1 text-xs rounded-sm font-medium transition-colors ${quarter === q ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-600'}`}>
              Q{q}
            </button>
          ))}
        </div>
        {data && <span className="text-xs text-slate-500 font-data">{data.total} submissions · ₹{data.total_amount?.toLocaleString('en-IN')}</span>}
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-48"><p className="text-slate-400 text-sm">Loading...</p></div>
      ) : !data || data.total === 0 ? (
        <div className="bg-slate-800/60 border border-slate-700 rounded-sm flex items-center justify-center h-48">
          <p className="text-slate-500 text-sm">No submissions for Q{quarter} {year}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
              <h3 className="font-heading text-sm font-semibold text-white mb-4">Submissions by Month — Q{quarter} {year}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.monthly_breakdown} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }}
                    tickFormatter={m => MONTH_NAMES[parseInt(m.split('-')[1]) - 1]} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
                  <Tooltip content={<Tip />} />
                  <Bar dataKey="count" name="Submissions" fill="#2563eb" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
              <h3 className="font-heading text-sm font-semibold text-white mb-4">Status Breakdown</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}>
                    {data.status_breakdown.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || CHART_COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={<Tip />} />
                  <Legend formatter={v => <span className="text-[10px] text-slate-300 font-data capitalize">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
            <h3 className="font-heading text-sm font-semibold text-white mb-4">Top Submitters</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.top_submitters} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} width={75} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="count" name="Submissions" fill="#8b5cf6" radius={[0, 2, 2, 0]} barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ─── YEARLY TAB ─────────────────────────────────────────────────
function YearlyTab({ formId, showTest }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/form-dashboard/${formId}/yearly`, { params: { show_test: showTest } })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [formId, showTest]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-slate-400 text-sm">Loading...</p></div>;
  if (!data || data.years.length === 0) return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-sm flex items-center justify-center h-48">
      <p className="text-slate-500 text-sm">No yearly data available</p>
    </div>
  );

  const monthMap = {};
  MONTH_NAMES.forEach((m, i) => { monthMap[String(i + 1).padStart(2, '0')] = { month: m }; });
  data.years.forEach(yr => {
    yr.monthly.forEach(m => {
      const mo = m.month.split('-')[1];
      if (monthMap[mo]) { monthMap[mo][yr.year + ' count'] = m.count; }
    });
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.years.map(yr => (
          <div key={yr.year} className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
            <p className="font-data text-xs text-slate-400 mb-1">{yr.year}</p>
            <p className="font-data text-xl font-bold text-white">{yr.total} submissions</p>
            <p className="font-data text-xs text-emerald-400 mt-1">₹{yr.total_amount?.toLocaleString('en-IN')}</p>
          </div>
        ))}
      </div>
      <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-4">
        <h3 className="font-heading text-sm font-semibold text-white mb-4">Year-over-Year Submissions by Month</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={Object.values(monthMap)} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} />
            <Tooltip content={<Tip />} />
            <Legend formatter={v => <span className="text-[10px] text-slate-300 font-data">{v}</span>} />
            {data.years.map((yr, i) => (
              <Bar key={yr.year} dataKey={`${yr.year} count`} name={`${yr.year}`} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[2, 2, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── REPORTS TAB ────────────────────────────────────────────────
function ReportsTab({ formId, showTest }) {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/submissions', { params: { form_id: formId, page, page_size: 20, show_test: showTest } })
      .then(r => { setSubmissions(r.data.submissions); setTotal(r.data.total); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [formId, page, showTest]);

  const exportCSV = () => {
    if (!submissions.length) return;
    const allKeys = [...new Set(submissions.flatMap(s => Object.keys(s.mapped_data || {})))];
    const headers = ['ID', 'Status', 'Priority', 'Created At', ...allKeys];
    const rows = submissions.map(s => [s.id, s.status, s.priority || '', s.created_at, ...allKeys.map(k => s.mapped_data?.[k] || '')]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `submissions-${formId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-data text-sm text-slate-400">{total} submission{total !== 1 ? 's' : ''}</p>
        <Button size="sm" variant="outline" className="rounded-sm text-xs bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 gap-1.5" onClick={exportCSV} data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>
      <div className="bg-slate-800/60 border border-slate-700 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><p className="text-slate-400 text-sm">Loading...</p></div>
        ) : submissions.length === 0 ? (
          <div className="flex items-center justify-center h-32"><p className="text-slate-500 text-sm">No submissions</p></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                {['Submitter', 'Status', 'Priority', 'Received'].map(h => (
                  <TableHead key={h} className="text-[9px] uppercase tracking-wider text-slate-400">{h}</TableHead>
                ))}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map(s => (
                <TableRow key={s.id} className="border-slate-700 hover:bg-slate-700/40 cursor-pointer" onClick={() => window.open(`/submissions/${s.id}`, '_blank')}>
                  <TableCell className="text-sm text-slate-300">{s.submitted_by_name}</TableCell>
                  <TableCell><Badge className={`text-[9px] rounded-sm ${STATUS_BG[s.status] || 'bg-slate-700 text-slate-300'}`}>{s.status}</Badge></TableCell>
                  <TableCell className="font-data text-xs text-slate-400 capitalize">{s.priority || '--'}</TableCell>
                  <TableCell className="font-data text-xs text-slate-500">{formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</TableCell>
                  <TableCell><ExternalLink className="w-3 h-3 text-slate-600" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {total > 20 && (
        <div className="flex items-center justify-between">
          <p className="font-data text-xs text-slate-500">Page {page} of {Math.ceil(total / 20)}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-sm bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 h-7 text-xs">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} className="rounded-sm bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 h-7 text-xs">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────
export default function FormDashboardPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [showTest, setShowTest] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [dashConfig, setDashConfig] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = useCallback(() => {
    setLoadingOverview(true);
    Promise.all([
      api.get(`/form-dashboard/${formId}/overview`, { params: { year: yearFilter !== 0 ? yearFilter : undefined, show_test: showTest } }),
      api.get(`/form-dashboard/${formId}/config`)
    ]).then(([ovRes, cfgRes]) => {
      setOverviewData(ovRes.data);
      setDashConfig(cfgRes.data);
    }).catch(() => {}).finally(() => setLoadingOverview(false));
  }, [formId, yearFilter, showTest, refreshKey]);

  useEffect(() => { loadData(); }, [loadData]);

  const formName = dashConfig?.dashboard_title || overviewData?.form?.name || 'Form Dashboard';
  const availableYears = overviewData?.available_years || [];
  const availableFields = overviewData?.available_fields || [];
  const currentYear = new Date().getFullYear();

  return (
    <div className="flex h-screen bg-[#0b1120] overflow-hidden" data-testid="form-dashboard-page">
      {/* Sidebar */}
      <aside className="w-52 bg-[#0f1629] border-r border-slate-800 flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-6 h-6 rounded-sm bg-blue-600 flex items-center justify-center flex-shrink-0">
              <BarChart2 className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-white text-xs font-bold truncate">SalesAudit Pro</p>
          </div>
          <p className="text-slate-500 text-[10px] pl-8 truncate leading-tight" title={formName}>{formName}</p>
          {dashConfig?.period_label && (
            <p className="text-blue-400/70 text-[9px] pl-8 mt-0.5 truncate">{dashConfig.period_label}</p>
          )}
        </div>

        <nav className="flex-1 p-2 space-y-0.5 mt-2">
          {NAV.map(item => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs font-medium transition-all duration-150 ${
                activeTab === item.id
                  ? item.id === 'settings' ? 'bg-slate-700 text-slate-100' : 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`}
            >
              <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
              {item.label}
              {item.id === 'settings' && !dashConfig?.configured && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" title="Not configured" />
              )}
            </button>
          ))}
        </nav>

        <div className="p-2 border-t border-slate-800">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs text-slate-500 hover:bg-slate-800 hover:text-slate-300 transition-colors"
            data-testid="back-to-main"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            Back to Command Center
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-12 bg-[#0f1629] border-b border-slate-800 flex items-center px-5 gap-3 flex-shrink-0">
          <h1 className="font-heading text-sm font-semibold text-white flex-1 truncate">{formName}</h1>
          <Select value={String(yearFilter)} onValueChange={v => setYearFilter(Number(v))}>
            <SelectTrigger className="w-28 h-7 rounded-sm bg-slate-800 border-slate-700 text-slate-200 text-xs" data-testid="year-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="0" className="text-slate-200 text-xs focus:bg-slate-700">All Years</SelectItem>
              {(availableYears.length > 0 ? availableYears : [currentYear, currentYear - 1]).map(y => (
                <SelectItem key={y} value={String(y)} className="text-slate-200 text-xs focus:bg-slate-700">{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setShowTest(v => !v)}
            className={`h-7 px-2.5 rounded-sm text-[10px] font-medium transition-colors border flex items-center gap-1.5 ${showTest ? 'bg-amber-500/20 border-amber-500/40 text-amber-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'}`}
            data-testid="form-dash-show-test"
          >
            {showTest ? 'Incl. Test' : 'Excl. Test'}
          </button>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="h-7 w-7 flex items-center justify-center rounded-sm bg-slate-800 border border-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingOverview ? 'animate-spin' : ''}`} />
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-5">
          <div className="mb-5">
            <h2 className="font-heading text-lg font-semibold text-white">
              {NAV.find(n => n.id === activeTab)?.label}
            </h2>
            <p className="font-data text-[11px] text-slate-500 mt-0.5">
              {overviewData?.form?.form_type && `${overviewData.form.form_type} · `}
              {overviewData?.kpis?.total} submissions
              {yearFilter !== 0 ? ` in ${yearFilter}` : ' (all time)'}
            </p>
          </div>

          {activeTab === 'overview' && <OverviewTab data={overviewData} config={dashConfig} />}
          {activeTab === 'quarterly' && <QuarterlyTab formId={formId} showTest={showTest} />}
          {activeTab === 'yearly' && <YearlyTab formId={formId} showTest={showTest} />}
          {activeTab === 'reports' && <ReportsTab formId={formId} showTest={showTest} />}
          {activeTab === 'settings' && (
            <SettingsTab
              formId={formId}
              config={dashConfig}
              availableFields={availableFields}
              onSaved={() => { setRefreshKey(k => k + 1); setActiveTab('overview'); }}
            />
          )}
        </main>
      </div>
    </div>
  );
}
